import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ESTADOS_REMESA,
  MODALIDADES_STAGING,
  interpretarZpl,
  type EtiquetaDibujada,
} from '@scm/contratos';
import { api } from '../lib/api.js';
import { entero, fechaHora } from '../lib/formato.js';
import {
  CLS_CARGA,
  CLS_EQP,
  CLS_REMESA,
  txt,
  TXT_CARGA,
  TXT_EQP,
  TXT_MODALIDAD,
  TXT_REMESA,
  TXT_TKT,
} from '../lib/enums.js';
import { opciones, type Opcion } from '../lib/opciones.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { columnasPresentes, ConmutadorVista, Kanban, useVista } from '../piezas/Kanban.jsx';
import { Cargando, Error as AvisoError, Toast, Vacio } from '../piezas/Estados.jsx';
import { useSesion } from '../piezas/Sesion.jsx';

/**
 * Remesas de carga.
 *
 * La pantalla de operacion es donde ocurre el trabajo: un renglon por equipo,
 * y las acciones por LOTE. Un lote de 500 equipos son 500 tickets, y una
 * pantalla que obligue a abrirlos uno por uno no se usa.
 */

interface RemesaFila {
  id: string;
  folio: string;
  cliente: string;
  proyecto: string;
  proyecto_nombre: string;
  modalidad: string;
  facilidad: string | null;
  imagen: string;
  imagen_nombre: string;
  so: string;
  version: string;
  id_imagen: string;
  idc: string | null;
  estado: string;
  abierta_en: string;
  cerrada_en: string | null;
  nota: string | null;
  equipos: number;
  cargados: number;
  con_error: number;
  pendientes: number;
  evidencias_cargadas: number;
}

interface RenglonRemesa {
  id: string;
  equipo_id: string;
  serie: string;
  marca: string;
  modelo: string;
  tipo_equipo: string;
  resultado: string;
  validado_en: string | null;
  nota: string | null;
  equipo_estado: string;
  sitio: string | null;
  ticket: string | null;
  ticket_estado: string | null;
}

interface DetalleRemesa extends RemesaFila {
  renglones: RenglonRemesa[];
  evidencias: { id: string; nombre: string; mime: string; bytes: number; creado_en: string }[];
}

export function Remesas(): JSX.Element {
  const [vista, ponerVista] = useVista('remesas');
  const [filas, setFilas] = useState<RemesaFila[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [alta, setAlta] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const { puede } = useSesion();
  const navegar = useNavigate();

  useEffect(() => {
    setFilas(null);
    api
      .obtener<RemesaFila[]>('/v1/remesas')
      .then(({ datos }) => setFilas(datos))
      .catch(setError);
  }, [recarga]);

  return (
    <>
      <Pagehead />

      <div className="fila wrap" style={{ marginBottom: 12 }}>
        <ConmutadorVista vista={vista} poner={ponerVista} />
        <span className="crece" />
        {puede('MOD_STAGING', 'escribir') && (
          <button
            type="button"
            className="btn pri"
            onClick={() => setAlta(true)}
            data-prueba="nueva-remesa"
          >
            + Abrir remesa
          </button>
        )}
      </div>

      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {filas === null ? (
          <Cargando que="las remesas" />
        ) : filas.length === 0 ? (
          <Vacio
            mensaje="Todavia no hay remesas"
            ayuda="Una remesa es un lote de carga: una imagen, un conjunto de equipos, una evidencia."
            icono="💽"
          />
        ) : vista === 'kanban' ? (
          <Kanban
            items={filas}
            estadoDe={(r) => r.estado}
            claveDe={(r) => r.folio}
            columnas={columnasPresentes(
              filas.map((r) => r.estado),
              [{ estados: ESTADOS_REMESA }],
            )}
            etiquetas={TXT_REMESA}
            clases={CLS_REMESA}
            alAbrir={(r) => void navegar(`/remesas/${r.folio}`)}
            tarjeta={(r) => (
              <>
                <div className="folio">{r.folio}</div>
                <b>{r.proyecto_nombre ?? r.proyecto}</b>
                <div className="kan-pie">
                  <span className="muted">{txt(TXT_MODALIDAD, r.modalidad)}</span>
                  <span className="muted">{r.equipos} equipos</span>
                </div>
              </>
            )}
          />
        ) : (
          <table data-prueba="tabla-remesas">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Imagen</th>
                <th>Proyecto</th>
                <th className="num">Equipos</th>
                <th className="num">Cargados</th>
                <th className="num">Con error</th>
                <th className="num">Pendientes</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => void navegar(`/remesas/${r.folio}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="folio">{r.folio}</td>
                  <td>
                    {r.imagen_nombre}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {r.so} {r.version} · {r.id_imagen}
                    </div>
                  </td>
                  <td>
                    {r.proyecto}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {txt(TXT_MODALIDAD, r.modalidad)}
                    </div>
                  </td>
                  <td className="num">{entero(r.equipos)}</td>
                  <td className="num">{entero(r.cargados)}</td>
                  <td
                    className="num"
                    style={r.con_error > 0 ? { color: 'var(--bad-ac)' } : undefined}
                  >
                    {entero(r.con_error)}
                  </td>
                  <td className="num">{entero(r.pendientes)}</td>
                  <td>
                    <span className={`tag ${CLS_REMESA[r.estado] ?? 't-mute'}`}>
                      {txt(TXT_REMESA, r.estado)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {alta && (
        <AltaRemesa
          alCerrar={() => setAlta(false)}
          alGuardar={(folio) => {
            setAlta(false);
            setRecarga((n) => n + 1);
            void navegar(`/remesas/${folio}`);
          }}
        />
      )}
    </>
  );
}

function AltaRemesa({
  alCerrar,
  alGuardar,
}: {
  alCerrar: () => void;
  alGuardar: (folio: string) => void;
}): JSX.Element {
  const [proyectos, setProyectos] = useState<Opcion[]>([]);
  const [imagenes, setImagenes] = useState<Opcion[]>([]);
  const [almacenes, setAlmacenes] = useState<Opcion[]>([]);
  const [idcs, setIdcs] = useState<Opcion[]>([]);
  const [proyecto, setProyecto] = useState('');
  const [imagen, setImagen] = useState('');
  const [modalidad, setModalidad] = useState<string>('STG_FACILIDAD');
  const [facilidad, setFacilidad] = useState('');
  const [idc, setIdc] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    opciones('proyectos').then(setProyectos).catch(setError);
    opciones('imagenes-sistema').then(setImagenes).catch(setError);
    opciones('almacenes').then(setAlmacenes).catch(setError);
    opciones('idc').then(setIdcs).catch(setError);
  }, []);

  async function guardar(): Promise<void> {
    setGuardando(true);
    setError(null);
    try {
      const { datos } = await api.enviar<{ folio: string }>('/v1/remesas', {
        proyecto_id: proyecto,
        modalidad,
        imagen_so_id: imagen,
        ...(modalidad === 'STG_FACILIDAD' && facilidad !== '' ? { almacen_id: facilidad } : {}),
        ...(idc === '' ? {} : { idc_id: idc }),
      });
      alGuardar(datos.folio);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <aside className="panel-lado" role="dialog" aria-modal="true" data-prueba="panel-remesa">
      <header className="panel-h">
        <h2>Abrir remesa de carga</h2>
        <button type="button" className="icobtn" onClick={alCerrar} aria-label="Cerrar">
          ✕
        </button>
      </header>
      <div className="panel-b">
        {error !== null && <AvisoError error={error} />}
        <div className="grid campos">
          <label className="campo">
            <span>
              Proyecto <b>*</b>
            </span>
            <select value={proyecto} onChange={(ev) => setProyecto(ev.target.value)}>
              <option value="">Elige…</option>
              {proyectos
                .filter((p) => p.activo)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.etiqueta}
                  </option>
                ))}
            </select>
          </label>
          <label className="campo">
            <span>
              Imagen <b>*</b>
            </span>
            <select value={imagen} onChange={(ev) => setImagen(ev.target.value)}>
              <option value="">Elige…</option>
              {imagenes
                .filter((i) => i.activo)
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.etiqueta}
                  </option>
                ))}
            </select>
            <small className="muted">
              Del mismo cliente que el proyecto: el servidor rechaza la de otro.
            </small>
          </label>
          <label className="campo">
            <span>Modalidad</span>
            <select value={modalidad} onChange={(ev) => setModalidad(ev.target.value)}>
              {MODALIDADES_STAGING.map((m) => (
                <option key={m} value={m}>
                  {txt(TXT_MODALIDAD, m)}
                </option>
              ))}
            </select>
          </label>
          {modalidad === 'STG_FACILIDAD' && (
            <label className="campo">
              <span>Facilidad</span>
              <select value={facilidad} onChange={(ev) => setFacilidad(ev.target.value)}>
                <option value="">Elige…</option>
                {almacenes
                  .filter((a) => a.activo)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.etiqueta}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <label className="campo">
            <span>IDC que ejecuta</span>
            <select value={idc} onChange={(ev) => setIdc(ev.target.value)}>
              <option value="">Sin asignar</option>
              {idcs
                .filter((i) => i.activo)
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.etiqueta}
                  </option>
                ))}
            </select>
            <small className="muted">Un IDC, igual que en campo.</small>
          </label>
        </div>
      </div>
      <footer className="panel-f">
        <button type="button" className="btn ghost" onClick={alCerrar}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn pri"
          disabled={proyecto === '' || imagen === '' || guardando}
          onClick={() => void guardar()}
          data-prueba="guardar-remesa"
        >
          {guardando ? 'Abriendo…' : 'Abrir remesa'}
        </button>
      </footer>
    </aside>
  );
}

/* --------------------- La pantalla de operacion -------------------------- */

export function Remesa(): JSX.Element {
  const { folio = '' } = useParams();
  const [d, setD] = useState<DetalleRemesa | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [agregando, setAgregando] = useState(false);
  const [etiqueta, setEtiqueta] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const { puede } = useSesion();

  const cargar = useCallback(() => {
    api
      .obtener<DetalleRemesa>(`/v1/remesas/${folio}`)
      .then(({ datos }) => {
        setD(datos);
        setMarcados(new Set());
      })
      .catch(setError);
  }, [folio]);

  useEffect(cargar, [cargar]);

  const abierta = d !== null && d.estado !== 'REM_CERRADA';
  const escribe = puede('MOD_STAGING', 'escribir');

  async function accion(hacer: () => Promise<string>): Promise<void> {
    setTrabajando(true);
    setError(null);
    try {
      setAviso(await hacer());
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  async function marcarCarga(
    resultado: 'CAR_OK' | 'CAR_ERROR',
    equipos: string[],
  ): Promise<string> {
    /* El error EXIGE nota: es lo que se le reclama al proveedor, y un equipo
       marcado con error y sin decir por que no sirve para reclamar nada. */
    let nota: string | null = null;
    if (resultado === 'CAR_ERROR') {
      nota = window.prompt('Por que fallo la carga? Esto es lo que se le reclama al proveedor.');
      if (nota === null || nota.trim() === '') return 'Sin nota no se marca el error.';
    }
    const { datos } = await api.enviar<{ equipos: number }>(`/v1/remesas/${folio}/lote`, {
      equipos,
      resultado,
      ...(nota === null ? {} : { nota: nota.trim() }),
    });
    return `${entero(datos.equipos)} equipos marcados.`;
  }

  if (error !== null && d === null) return <AvisoError error={error} />;
  if (d === null) return <Cargando que="la remesa" />;

  const pendientes = d.renglones.filter((r) => r.resultado === 'CAR_PENDIENTE');

  return (
    <>
      <Pagehead />

      <div className="fila wrap" style={{ marginBottom: 12, gap: 8 }}>
        <Link to="/remesas" className="btn ghost sm">
          ← Remesas
        </Link>
        <span className="crece" />
        <span className={`tag ${CLS_REMESA[d.estado] ?? 't-mute'}`} data-prueba="estado-remesa">
          {txt(TXT_REMESA, d.estado)}
        </span>
      </div>

      {error !== null && <AvisoError error={error} />}

      {/* Encabezado: la imagen que se carga, quien ejecuta, y donde. */}
      <div className="card" data-prueba="cabeza-remesa">
        <div className="fila wrap" style={{ gap: 24 }}>
          <Dato t="Folio" v={d.folio} mono />
          <Dato t="Imagen" v={`${d.imagen_nombre} (${d.imagen})`} />
          <Dato t="Sistema" v={`${d.so} ${d.version}`} />
          <Dato t="Id de imagen" v={d.id_imagen} mono />
          <Dato t="IDC" v={d.idc ?? 'Sin asignar'} />
          <Dato t="Modalidad" v={txt(TXT_MODALIDAD, d.modalidad)} />
          <Dato t="Facilidad" v={d.facilidad ?? '—'} />
          <Dato t="Abierta" v={fechaHora(d.abierta_en)} />
          {d.cerrada_en !== null && <Dato t="Cerrada" v={fechaHora(d.cerrada_en)} />}
        </div>
      </div>

      <div className="grid kpis" style={{ marginTop: 12 }} data-prueba="indicadores-remesa">
        <Indicador t="En la remesa" v={d.equipos} ico="🖥" />
        <Indicador t="Cargados y validados" v={d.cargados} ico="✅" />
        <Indicador t="Con error" v={d.con_error} ico="⚠" malo />
        <Indicador t="Pendientes" v={d.pendientes} ico="⏳" />
        <Indicador t="Evidencias" v={d.evidencias_cargadas} ico="📷" />
      </div>

      {escribe && abierta && (
        <div className="card fila wrap" style={{ marginTop: 12, gap: 8 }}>
          <button
            type="button"
            className="btn sm"
            onClick={() => setAgregando(true)}
            data-prueba="agregar-equipos"
          >
            + Agregar equipos virgenes
          </button>

          <SubirEvidencia
            folio={folio}
            alSubir={() => {
              setAviso('Evidencia cargada.');
              cargar();
            }}
          />

          <span className="crece" />

          {marcados.size > 0 ? (
            <>
              <b data-prueba="marcados-remesa">{entero(marcados.size)} marcados</b>
              <button
                type="button"
                className="btn sm"
                disabled={trabajando}
                onClick={() => void accion(() => marcarCarga('CAR_OK', [...marcados]))}
              >
                Marcar cargados
              </button>
              <button
                type="button"
                className="btn sm"
                disabled={trabajando}
                onClick={() => void accion(() => marcarCarga('CAR_ERROR', [...marcados]))}
                data-prueba="marcar-error"
              >
                Marcar con error
              </button>
            </>
          ) : (
            pendientes.length > 0 && (
              <button
                type="button"
                className="btn sm"
                disabled={trabajando}
                onClick={() =>
                  void accion(() =>
                    marcarCarga(
                      'CAR_OK',
                      pendientes.map((r) => r.equipo_id),
                    ),
                  )
                }
                data-prueba="marcar-todo"
              >
                Marcar TODO como cargado ({entero(pendientes.length)})
              </button>
            )
          )}

          <button
            type="button"
            className="btn pri sm"
            disabled={trabajando}
            onClick={() =>
              void accion(async () => {
                const { datos } = await api.enviar<{ tickets_avanzados: number }>(
                  `/v1/remesas/${folio}/cerrar`,
                );
                return `Remesa cerrada. ${entero(datos.tickets_avanzados)} tickets avanzaron.`;
              })
            }
            data-prueba="cerrar-remesa"
          >
            Cerrar remesa
          </button>
        </div>
      )}

      {/* El cierre exige las dos cosas, y se dice ANTES de intentarlo. */}
      {escribe && abierta && (d.pendientes > 0 || d.evidencias_cargadas === 0) && (
        <div className="card" style={{ marginTop: 12 }} data-prueba="falta-para-cerrar">
          <b>Para cerrar la remesa falta:</b>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {d.evidencias_cargadas === 0 && (
              <li>Subir la evidencia fotografica de la validacion.</li>
            )}
            {d.pendientes > 0 && (
              <li>Capturar el resultado de {entero(d.pendientes)} equipos sin marcar.</li>
            )}
          </ul>
        </div>
      )}

      <div className="card p0" style={{ marginTop: 12 }}>
        {d.renglones.length === 0 ? (
          <Vacio
            mensaje="La remesa no tiene equipos"
            ayuda="Agrega equipos virgenes del proyecto para empezar a cargar."
            icono="🖥"
          />
        ) : (
          <table data-prueba="tabla-renglones">
            <thead>
              <tr>
                <th>Serie</th>
                <th>Resultado</th>
                <th>Validado</th>
                <th>Equipo</th>
                <th>Ticket</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.renglones.map((r) => (
                <tr key={r.id}>
                  <td>
                    <label className="fila" style={{ gap: 8 }}>
                      {escribe && abierta && r.resultado === 'CAR_PENDIENTE' && (
                        <input
                          type="checkbox"
                          checked={marcados.has(r.equipo_id)}
                          onChange={(ev) => {
                            const copia = new Set(marcados);
                            if (ev.target.checked) copia.add(r.equipo_id);
                            else copia.delete(r.equipo_id);
                            setMarcados(copia);
                          }}
                          data-prueba={`marca-${r.serie}`}
                        />
                      )}
                      <span className="folio">{r.serie}</span>
                    </label>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {r.marca} {r.modelo}
                    </div>
                  </td>
                  <td>
                    <span className={`tag ${CLS_CARGA[r.resultado] ?? 't-mute'}`}>
                      {txt(TXT_CARGA, r.resultado)}
                    </span>
                    {r.nota !== null && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {r.nota}
                      </div>
                    )}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {r.validado_en === null ? '—' : fechaHora(r.validado_en)}
                  </td>
                  <td>
                    <span className={`tag ${CLS_EQP[r.equipo_estado] ?? 't-mute'}`}>
                      {txt(TXT_EQP, r.equipo_estado)}
                    </span>
                  </td>
                  <td>
                    {r.ticket === null ? (
                      '—'
                    ) : (
                      <>
                        <Link to={`/tickets/${r.ticket}`} className="folio">
                          {r.ticket}
                        </Link>
                        <div className="muted" style={{ fontSize: 11 }}>
                          {txt(TXT_TKT, r.ticket_estado ?? '')}
                        </div>
                      </>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn sm ghost"
                      onClick={() => setEtiqueta(r.equipo_id)}
                      data-prueba={`etiqueta-${r.serie}`}
                    >
                      Etiqueta
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {agregando && (
        <AgregarEquipos
          folio={folio}
          alCerrar={() => setAgregando(false)}
          alGuardar={(m) => {
            setAgregando(false);
            setAviso(m);
            cargar();
          }}
        />
      )}
      {etiqueta !== null && (
        <PanelEtiqueta equipoId={etiqueta} alCerrar={() => setEtiqueta(null)} />
      )}
      {aviso !== null && <Toast mensaje={aviso} />}
    </>
  );
}

function Dato({ t, v, mono = false }: { t: string; v: string; mono?: boolean }): JSX.Element {
  return (
    <div>
      <div className="muted" style={{ fontSize: 11 }}>
        {t}
      </div>
      <div className={mono ? 'folio' : undefined}>{v}</div>
    </div>
  );
}

function Indicador({
  t,
  v,
  ico,
  malo = false,
}: {
  t: string;
  v: number;
  ico: string;
  malo?: boolean;
}): JSX.Element {
  return (
    <div className="card kpi">
      <span className="ico" aria-hidden="true">
        {ico}
      </span>
      <span className="val">
        <span>{t}</span>
        <b style={malo && v > 0 ? { color: 'var(--bad-ac)' } : undefined}>{entero(v)}</b>
      </span>
    </div>
  );
}

/** Sube la evidencia de la validacion. Sin ella la remesa no se valida. */
function SubirEvidencia({ folio, alSubir }: { folio: string; alSubir: () => void }): JSX.Element {
  const [error, setError] = useState<unknown>(null);

  async function subir(archivo: File): Promise<void> {
    setError(null);
    try {
      const bytes = new Uint8Array(await archivo.arrayBuffer());
      let binario = '';
      for (const b of bytes) binario += String.fromCharCode(b);
      await api.enviar(`/v1/remesas/${folio}/evidencias`, {
        nombre: archivo.name,
        mime: archivo.type,
        contenido_base64: btoa(binario),
      });
      alSubir();
    } catch (e) {
      setError(e);
    }
  }

  return (
    <>
      <label className="btn sm" data-prueba="subir-evidencia">
        📷 Subir evidencia
        <input
          type="file"
          accept="image/*,application/pdf"
          style={{ display: 'none' }}
          onChange={(ev) => {
            const f = ev.target.files?.[0];
            if (f) void subir(f);
          }}
        />
      </label>
      {error !== null && <AvisoError error={error} />}
    </>
  );
}

/** Mete equipos virgenes del proyecto a la remesa. */
function AgregarEquipos({
  folio,
  alCerrar,
  alGuardar,
}: {
  folio: string;
  alCerrar: () => void;
  alGuardar: (mensaje: string) => void;
}): JSX.Element {
  const [libres, setLibres] = useState<{ id: string; serie: string; modelo: string }[] | null>(
    null,
  );
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    /* Solo los VIRGENES y los que fallaron: un equipo que ya trae imagen no
       se vuelve a cargar por descuido, y uno con error vuelve al proceso solo
       si alguien lo pone aqui a proposito. */
    api
      .obtener<{ id: string; serie: string; modelo: string }[]>(
        '/v1/equipos?estado=EQP_VIRGEN&limite=200',
      )
      .then(({ datos }) => setLibres(datos))
      .catch(setError);
  }, []);

  async function guardar(): Promise<void> {
    setGuardando(true);
    try {
      const { datos } = await api.enviar<{ equipos: number; pedidos: number }>(
        `/v1/remesas/${folio}/renglones`,
        { equipos: [...marcados] },
      );
      alGuardar(
        datos.equipos === datos.pedidos
          ? `${entero(datos.equipos)} equipos agregados.`
          : `${entero(datos.equipos)} de ${entero(datos.pedidos)} agregados: los demas no estaban virgenes o ya estaban en la remesa.`,
      );
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <aside className="panel-lado" role="dialog" aria-modal="true" data-prueba="panel-agregar">
      <header className="panel-h">
        <h2>Agregar equipos virgenes</h2>
        <button type="button" className="icobtn" onClick={alCerrar} aria-label="Cerrar">
          ✕
        </button>
      </header>
      <div className="panel-b">
        {error !== null && <AvisoError error={error} />}
        {libres === null ? (
          <Cargando que="los equipos" />
        ) : libres.length === 0 ? (
          <Vacio
            mensaje="No hay equipos virgenes"
            ayuda="Dales de alta con una recepcion de pallets."
            icono="🖥"
          />
        ) : (
          <>
            <div className="fila" style={{ gap: 8, marginBottom: 8 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => setMarcados(new Set(libres.map((l) => l.id)))}
              >
                Marcar todos ({entero(libres.length)})
              </button>
              <button type="button" className="btn sm ghost" onClick={() => setMarcados(new Set())}>
                Ninguno
              </button>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {libres.map((l) => (
                <li key={l.id}>
                  <label className="fila" style={{ gap: 8, padding: '4px 0' }}>
                    <input
                      type="checkbox"
                      checked={marcados.has(l.id)}
                      onChange={(ev) => {
                        const copia = new Set(marcados);
                        if (ev.target.checked) copia.add(l.id);
                        else copia.delete(l.id);
                        setMarcados(copia);
                      }}
                    />
                    <span className="folio">{l.serie}</span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {l.modelo}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <footer className="panel-f">
        <button type="button" className="btn ghost" onClick={alCerrar}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn pri"
          disabled={marcados.size === 0 || guardando}
          onClick={() => void guardar()}
          data-prueba="guardar-equipos"
        >
          {guardando ? 'Agregando…' : `Agregar ${entero(marcados.size)}`}
        </button>
      </footer>
    </aside>
  );
}

/* ------------------------------ Etiqueta --------------------------------- */

interface Etiqueta {
  equipo: { serie: string; marca: string; modelo: string };
  plantilla: {
    id: string;
    clave: string;
    nombre: string;
    ancho_mm: number;
    alto_mm: number;
    dpi: number;
  };
  zpl: string;
  impresion_directa: boolean;
}

/**
 * La etiqueta de activo fijo: vista previa DIBUJADA y el ZPL a la vista.
 *
 * La vista previa es una aproximacion a partir de los comandos de texto,
 * posicion y codigo de barras. Lo dice en pantalla: una vista previa que
 * finge ser exacta invita a mandar a imprimir sin revisar.
 */
function PanelEtiqueta({
  equipoId,
  alCerrar,
}: {
  equipoId: string;
  alCerrar: () => void;
}): JSX.Element {
  const [e, setE] = useState<Etiqueta | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    api
      .obtener<Etiqueta>(`/v1/equipos/${equipoId}/etiqueta`)
      .then(({ datos }) => setE(datos))
      .catch(setError);
  }, [equipoId]);

  function descargar(): void {
    if (e === null) return;
    const url = URL.createObjectURL(new Blob([e.zpl], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `etiqueta-${e.equipo.serie}.zpl`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <aside className="panel-lado" role="dialog" aria-modal="true" data-prueba="panel-etiqueta">
      <header className="panel-h">
        <h2>Etiqueta de activo fijo</h2>
        <button type="button" className="icobtn" onClick={alCerrar} aria-label="Cerrar">
          ✕
        </button>
      </header>
      <div className="panel-b">
        {error !== null && <AvisoError error={error} />}
        {e === null && error === null ? (
          <Cargando que="la etiqueta" />
        ) : e === null ? (
          <></>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              {e.equipo.marca} {e.equipo.modelo} · <span className="folio">{e.equipo.serie}</span>
              <br />
              Plantilla: {e.plantilla.nombre} ({e.plantilla.ancho_mm}×{e.plantilla.alto_mm} mm,{' '}
              {e.plantilla.dpi} dpi)
            </p>

            <h4>Vista previa</h4>
            <VistaPreviaZpl zpl={e.zpl} />
            <small className="muted">
              Dibujo aproximado a partir de los comandos de texto, posicion y codigo de barras. Lo
              que sale de la impresora manda.
            </small>

            <h4>ZPL</h4>
            <pre
              style={{
                background: 'var(--bg-2)',
                padding: 10,
                borderRadius: 6,
                overflow: 'auto',
                maxHeight: 220,
                fontSize: 12,
              }}
              data-prueba="zpl"
            >
              {e.zpl}
            </pre>

            {!e.impresion_directa && (
              /* Dicho y no escondido: la impresora esta en la facilidad, no
                 donde corre el servidor. */
              <p className="muted">
                Esta instalacion no manda el ZPL a la Zebra: descargalo y mandalo desde la
                facilidad. Pendiente declarado.
              </p>
            )}
          </>
        )}
      </div>
      <footer className="panel-f">
        <button type="button" className="btn ghost" onClick={alCerrar}>
          Cerrar
        </button>
        <button
          type="button"
          className="btn"
          disabled={e === null}
          onClick={() => {
            if (e !== null) {
              void navigator.clipboard.writeText(e.zpl).then(() => setCopiado(true));
            }
          }}
        >
          {copiado ? 'Copiado' : 'Copiar ZPL'}
        </button>
        <button
          type="button"
          className="btn pri"
          disabled={e === null}
          onClick={descargar}
          data-prueba="descargar-zpl"
        >
          Descargar .zpl
        </button>
      </footer>
    </aside>
  );
}

function VistaPreviaZpl({ zpl }: { zpl: string }): JSX.Element {
  const d: EtiquetaDibujada = interpretarZpl(zpl);
  return (
    <>
      <svg
        viewBox={`0 0 ${d.ancho} ${d.alto}`}
        style={{
          width: '100%',
          maxWidth: 420,
          border: '1px solid var(--line)',
          borderRadius: 4,
          background: '#fff',
        }}
        role="img"
        aria-label="Vista previa de la etiqueta"
        data-prueba="vista-previa"
      >
        {d.elementos.map((el, i) =>
          el.tipo === 'texto' ? (
            <text
              key={i}
              x={el.x}
              y={el.y + el.alto}
              fontSize={el.alto}
              fontFamily="monospace"
              fill="#111"
            >
              {el.valor}
            </text>
          ) : (
            /* El codigo de barras se dibuja como barras parejas: no es el
               Code 128 real y no pretende serlo, pero deja ver que hay un
               codigo, donde y de que alto. */
            <g key={i}>
              {Array.from({ length: Math.min(el.valor.length * 6, 90) }, (_, j) => (
                <rect
                  key={j}
                  x={el.x + j * 3}
                  y={el.y}
                  width={j % 3 === 0 ? 2 : 1}
                  height={el.alto}
                  fill="#111"
                />
              ))}
              <text x={el.x} y={el.y + el.alto + 12} fontSize={11} fontFamily="monospace">
                {el.valor}
              </text>
            </g>
          ),
        )}
      </svg>
      {d.ignorados > 0 && (
        <div className="muted" style={{ fontSize: 11 }} data-prueba="zpl-ignorados">
          {d.ignorados} comandos del ZPL no se dibujan en la vista previa.
        </div>
      )}
    </>
  );
}
